import React, { useState, useCallback } from 'react';
import { Search, Package } from 'lucide-react';
import { useAllMaterials, useProductList } from '../../../hooks';
import { formatPrice } from '../../../utils/formatPrice';
import { MATERIAL_COLORS } from '../../../constants/models';
import '../../../css/model-builder.css';

// Pagination configuration for materials
const MATERIALS_PAGE_SIZE = 50;

const ComponentSearchPanel = ({ onAddComponent }) => {
    const [activeTab, setActiveTab] = useState('data'); // 'data' (materials) or 'products'
    const [searchQuery, setSearchQuery] = useState('');
    const [materialsOffset, setMaterialsOffset] = useState(0);
    const [allLoadedMaterials, setAllLoadedMaterials] = useState([]);
    const [hasMoreMaterials, setHasMoreMaterials] = useState(true);

    // Fetch materials with pagination from API (for Materials tab)
    const { data: materials, loading: materialsLoading } = useAllMaterials({
        enabled: activeTab === 'data',
        limit: MATERIALS_PAGE_SIZE,
        offset: materialsOffset,
        cache: true,
        cacheTTL: 5 * 60 * 1000,
    });

    // Accumulate loaded materials when new data arrives
    React.useEffect(() => {
        if (materials && materials.length > 0) {
            if (materialsOffset === 0) {
                setAllLoadedMaterials(materials);
            } else {
                setAllLoadedMaterials(prev => {
                    // Avoid duplicates by checking IDs
                    const existingIds = new Set(prev.map(m => m.id || m.code));
                    const newMaterials = materials.filter(m => !existingIds.has(m.id || m.code));
                    return [...prev, ...newMaterials];
                });
            }
            // Check if there are more materials to load
            setHasMoreMaterials(materials.length === MATERIALS_PAGE_SIZE);
        } else if (materials && materials.length === 0) {
            setHasMoreMaterials(false);
        }
    }, [materials, materialsOffset]);

    // Load more materials handler
    const loadMoreMaterials = useCallback(() => {
        if (!materialsLoading && hasMoreMaterials) {
            setMaterialsOffset(prev => prev + MATERIALS_PAGE_SIZE);
        }
    }, [materialsLoading, hasMoreMaterials]);

    // Fetch products list from API (for Products tab)
    const { data: productList, loading: productsLoading } = useProductList({
        enabled: activeTab === 'products',
        limit: 200,
        cache: true,
        cacheTTL: 2 * 60 * 1000,
    });

    const loading = activeTab === 'products' ? productsLoading : (materialsLoading && materialsOffset === 0);
    const loadingMore = activeTab === 'data' && materialsLoading && materialsOffset > 0;

    // Transform and filter materials for display (use accumulated materials)
    const filteredMaterials = allLoadedMaterials
        ? allLoadedMaterials.filter(m => m.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            (m.code && m.code.toLowerCase().includes(searchQuery.toLowerCase()))
        ).map((m, index) => ({
            id: m.id || m.code,
            name: m.name,
            price: m.last_value || 0,
            unit: m.value_unit || 'mt',
            currency: m.currency || 'USD',
            type: 'material',
            code: m.code,
            color: MATERIAL_COLORS[index % MATERIAL_COLORS.length],
        }))
        : [];

    // Transform and filter products for display
    const products = productList?.items || [];
    const filteredProducts = products
        .filter(p => p.name.toLowerCase().includes(searchQuery.toLowerCase()))
        .map(p => ({
            id: p.id,
            name: p.name,
            price: p.last_price_value || 0,
            unit: p.last_price_unit || 'unit',
            currency: p.last_price_currency || 'USD',
            type: 'product',
            color: '#8B5CF6',
            imageUrl: p.image_url || null,
        }));

    const filteredItems = activeTab === 'data' ? filteredMaterials : filteredProducts;

    return (
        <div className="component-search-panel">
            <h3 className="panel-title">COMPONENT SEARCH</h3>

            {/* Pill Tabs Selector */}
            <div className="panel-tabs">
                <button
                    className={`panel-tab ${activeTab === 'data' ? 'active' : ''}`}
                    onClick={() => setActiveTab('data')}
                >
                    Materials
                </button>
                <button
                    className={`panel-tab ${activeTab === 'products' ? 'active' : ''}`}
                    onClick={() => setActiveTab('products')}
                >
                    Products
                </button>
            </div>

            {/* Search */}
            <div className="panel-search">
                <Search size={16} className="search-icon" />
                <input
                    type="text"
                    className="search-input"
                    placeholder="Suche"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                />
            </div>

            {/* List */}
            <div className="panel-items">
                {loading ? (
                    <div className="empty-search">
                        Loading {activeTab === 'products' ? 'products' : 'materials'}...
                    </div>
                ) : filteredItems.length > 0 ? (
                    <>
                        {filteredItems.map(item => (
                            <div
                                key={item.id}
                                className="panel-item"
                                draggable
                                onDragStart={(e) => {
                                    e.dataTransfer.setData('application/json', JSON.stringify(item));
                                    e.dataTransfer.effectAllowed = 'copy';
                                }}
                                onClick={() => onAddComponent(item)}
                            >
                                {item.type === 'product' && (
                                    <div className="item-visual">
                                        {item.imageUrl ? (
                                            <img
                                                src={item.imageUrl}
                                                alt={item.name}
                                                className="item-visual__image"
                                            />
                                        ) : (
                                            <div className="item-visual__placeholder">
                                                <Package size={18} color="rgba(139, 92, 246, 0.6)" />
                                            </div>
                                        )}
                                    </div>
                                )}
                                <div className="item-info">
                                    <div className="item-header">
                                        <div className="item-name">{item.name}</div>
                                        <div className="item-price-stack">
                                            <div className="item-price">
                                                {formatPrice(item.price || 0)}
                                            </div>
                                            <div className="item-unit">{item.currency} / {item.unit}</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                        {/* Load More button for materials */}
                        {activeTab === 'data' && hasMoreMaterials && !searchQuery && (
                            <button
                                className="load-more-btn"
                                onClick={loadMoreMaterials}
                                disabled={loadingMore}
                                style={{ cursor: loadingMore ? 'wait' : 'pointer' }}
                            >
                                {loadingMore ? 'Loading...' : 'Load More Materials'}
                            </button>
                        )}
                    </>
                ) : (
                    <div className="empty-search">
                        {searchQuery
                            ? `No ${activeTab === 'products' ? 'products' : 'materials'} found matching "${searchQuery}"`
                            : `No ${activeTab === 'products' ? 'products' : 'materials'} available`
                        }
                    </div>
                )}
            </div>
        </div>
    );
};

export default ComponentSearchPanel;
